import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { type User, api, setUnauthorizedHandler } from "../../lib/api";

interface AuthContextValue {
  user: User | null;
  /** True only during the initial GET /auth/me on load — distinct from
   *  "signed out," so the app can show a loading state instead of flashing
   *  the login screen for a session that turns out to be valid. */
  isLoading: boolean;
  login: (usernameOrEmail: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Lets a profile-edit flow update the cached user without a round trip. */
  setUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// The single source of truth for "who is signed in," backed by the session
// cookie the backend manages — this component holds no token itself, only a
// cached copy of what GET /auth/me last said. Every other part of the app
// reads identity through useAuth() rather than calling api.auth.me() again.
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Registered once, for the lifetime of the app: any request anywhere
    // that comes back 401 (session expired, revoked, or the account was
    // disabled mid-session) drops the cached user, which is what makes the
    // login screen reappear without every call site needing to know that.
    setUnauthorizedHandler(() => setUser(null));

    let cancelled = false;
    api.auth
      .me()
      .then((me) => {
        if (!cancelled) setUser(me);
      })
      .catch(() => {
        // Not signed in yet, or the session already lapsed — either way,
        // the login screen is the correct thing to show, not an error.
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
      setUnauthorizedHandler(null);
    };
  }, []);

  const login = useCallback(async (usernameOrEmail: string, password: string) => {
    const me = await api.auth.login(usernameOrEmail, password);
    setUser(me);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.auth.logout();
    } finally {
      // Clear client-side regardless of whether the request itself
      // succeeded — the user's intent was to end up signed out, and the
      // backend's logout is already idempotent against a session that
      // turns out to be gone already.
      setUser(null);
    }
  }, []);

  return <AuthContext.Provider value={{ user, isLoading, login, logout, setUser }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
